import { type LatLng, makeFrame, type Vec2 } from './solarFacePacker';

type SolarPanelOutline = {
  id: string;
  /** Packer order: eave-left, eave-right, ridge-right, ridge-left. */
  corners: readonly LatLng[];
};

/** A gap wider than this many panel widths splits a row into two runs. */
const GAP_PANEL_WIDTHS = 1.5;
/** Panel centres within this many short sides of each other share a row. */
const ROW_SHORT_SIDES = 0.4;
/** Rows further apart than the face's closest pair by this much do not touch. */
const ROW_GAP_SLACK_M = 0.6;
/** Open roof a wire may cross beside a run: half the gap that splits runs. */
const BRIDGE = GAP_PANEL_WIDTHS / 2;

type Placed = {
  id: string;
  /** Along the row, eave-left to right. */
  u: number;
  /** Up the slope from the eave. */
  v: number;
  w: number;
  h: number;
  ground: Vec2;
  row: number;
};
type Run = { panels: Placed[]; row: number; lo: number; hi: number };
type Zone = { runs: Run[] };

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.y * b.y;
const unit = (a: Vec2): Vec2 | null => {
  const length = Math.hypot(a.x, a.y);
  return length > 1e-9 ? { x: a.x / length, y: a.y / length } : null;
};

/** The edges' shared direction, flipping any that point against the majority. */
const axisOf = (edges: Vec2[]): Vec2 | null => {
  const reference =
    unit(edges.reduce((sum, e) => ({ x: sum.x + e.x, y: sum.y + e.y }))) ??
    unit(edges[0]);
  if (!reference) return null;
  return unit(
    edges.reduce(
      (sum, e) => {
        const sign = dot(e, reference) < 0 ? -1 : 1;
        return { x: sum.x + sign * e.x, y: sum.y + sign * e.y };
      },
      { x: 0, y: 0 }
    )
  );
};

/**
 * The order an installer would wire one face's panels, snaking each zone of
 * stacked runs from the eave. Null when a panel lacks corners or the panels
 * give no usable row direction.
 */
export const solarSerpentinePanelOrder = (
  panels: readonly SolarPanelOutline[]
): string[] | null => {
  if (!panels.length) return [];
  if (panels.some((panel) => panel.corners.length < 4)) return null;
  const frame = makeFrame(panels[0].corners[0]);
  const outlines = panels.map((panel) => ({
    id: panel.id,
    points: panel.corners.slice(0, 4).map(frame.toM),
  }));
  const along = axisOf(outlines.map(({ points }) => sub(points[1], points[0])));
  const up = axisOf(outlines.map(({ points }) => sub(points[3], points[0])));
  if (!along || !up) return null;
  // Ground-projected axes of a pitched, rotated array need not be square.
  const det = along.x * up.y - along.y * up.x;
  if (Math.abs(det) < 0.1) return null;
  const local = (p: Vec2) => ({
    u: (p.x * up.y - p.y * up.x) / det,
    v: (along.x * p.y - along.y * p.x) / det,
  });

  const placed: Placed[] = outlines.map(({ id, points }) => {
    const ground = {
      x: points.reduce((sum, p) => sum + p.x, 0) / 4,
      y: points.reduce((sum, p) => sum + p.y, 0) / 4,
    };
    const centre = local(ground);
    return {
      id,
      u: centre.u,
      v: centre.v,
      w: Math.abs(local(sub(points[1], points[0])).u),
      h: Math.abs(local(sub(points[3], points[0])).v),
      ground,
      row: 0,
    };
  });

  const rows: Placed[][] = [];
  let lastV = -Infinity;
  for (const panel of [...placed].sort((a, b) => a.v - b.v || a.u - b.u)) {
    if (
      !rows.length ||
      panel.v - lastV > ROW_SHORT_SIDES * Math.min(panel.w, panel.h)
    )
      rows.push([]);
    panel.row = rows.length - 1;
    rows[rows.length - 1].push(panel);
    lastV = panel.v;
  }
  for (const row of rows) row.sort((a, b) => a.u - b.u);

  const edgeGap = (a: Placed, b: Placed) => b.u - b.w / 2 - (a.u + a.w / 2);
  const runsOf = (row: Placed[]): Run[] => {
    const runs: Placed[][] = [];
    for (const panel of row) {
      const current = runs.at(-1);
      const last = current?.at(-1);
      if (
        current &&
        last &&
        edgeGap(last, panel) <= GAP_PANEL_WIDTHS * Math.min(last.w, panel.w)
      )
        current.push(panel);
      else runs.push([panel]);
    }
    return runs.map((items) => ({
      panels: items,
      row: items[0].row,
      lo: items[0].u - items[0].w / 2,
      hi: items[items.length - 1].u + items[items.length - 1].w / 2,
    }));
  };
  const rowRuns = rows.map(runsOf);

  const bottom = (row: Placed[]) => Math.min(...row.map((p) => p.v - p.h / 2));
  const top = (row: Placed[]) => Math.max(...row.map((p) => p.v + p.h / 2));
  const rowGaps = rows
    .slice(1)
    .map((row, index) => bottom(row) - top(rows[index]));
  // One gap cannot tell tilt-frame row spacing from an obstruction band.
  const spacing = rowGaps.length > 1 ? Math.max(0, Math.min(...rowGaps)) : 0;
  const touchingGap = spacing + ROW_GAP_SLACK_M;
  const stacked = (below: Run, above: Run) =>
    above.row === below.row + 1 &&
    rowGaps[below.row] <= touchingGap &&
    above.lo < below.hi &&
    below.lo < above.hi;

  const zones: Zone[] = [];
  const zoneOf = new Map<Run, Zone>();
  let previous: Run[] = [];
  for (const runs of rowRuns) {
    for (const run of runs) {
      const below = previous.filter((item) => stacked(item, run));
      const only = below.length === 1 ? below[0] : null;
      const zone =
        only && runs.filter((item) => stacked(only, item)).length === 1
          ? zoneOf.get(only)
          : undefined;
      if (zone) zone.runs.push(run);
      else zones.push({ runs: [run] });
      zoneOf.set(run, zone ?? zones[zones.length - 1]);
    }
    previous = runs;
  }

  const variants = (zone: Zone): Placed[][] =>
    [true, false].flatMap((upward) =>
      [true, false].map((startLeft) =>
        (upward ? zone.runs : [...zone.runs].reverse()).flatMap((run, index) =>
          (index % 2 === 0) === startLeft
            ? run.panels
            : [...run.panels].reverse()
        )
      )
    );
  /** Inside a run of its row, or of either row at a seam where rows touch. */
  const onArray = (u: number, v: number) => {
    const covers = (index: number) =>
      rowRuns[index].some(
        (run) =>
          u >= run.lo - BRIDGE * run.panels[0].w &&
          u <= run.hi + BRIDGE * run.panels[run.panels.length - 1].w
      );
    return rows.some((row, index) => {
      if (v >= bottom(row) && v <= top(row)) return covers(index);
      const next = rows[index + 1];
      return (
        !!next &&
        v > top(row) &&
        v < bottom(next) &&
        rowGaps[index] <= touchingGap &&
        (covers(index) || covers(index + 1))
      );
    });
  };
  const step = 0.25 * Math.min(...placed.map((p) => Math.min(p.w, p.h)));
  /** Whether wiring a to b would run over open roof, such as an obstruction. */
  const crossesGap = (a: Placed, b: Placed) => {
    const samples = Math.ceil(Math.hypot(b.u - a.u, b.v - a.v) / step);
    for (let i = 1; i < samples; i++) {
      const t = i / samples;
      if (!onArray(a.u + (b.u - a.u) * t, a.v + (b.v - a.v) * t)) return true;
    }
    return false;
  };
  const distance = (a: Placed, b: Placed) =>
    Math.hypot(b.ground.x - a.ground.x, b.ground.y - a.ground.y);

  const routes = zones.flatMap((zone) =>
    variants(zone).map((panels) => ({ zone, panels }))
  );
  const path: Placed[] = [];
  const done = new Set<Zone>();
  while (done.size < zones.length) {
    const end = path.at(-1);
    const open = routes.filter((route) => !done.has(route.zone));
    const nearest = end
      ? open
          .map((route) => ({ route, cost: distance(end, route.panels[0]) }))
          .sort((a, b) => a.cost - b.cost)
          .map(({ route }) => route)
      : [];
    const next = end
      ? (nearest.find((route) => !crossesGap(end, route.panels[0])) ??
        nearest[0])
      : open.reduce((best, route) => {
          const a = route.panels[0];
          const b = best.panels[0];
          return a.row < b.row || (a.row === b.row && a.u < b.u) ? route : best;
        });
    if (!next) break;
    path.push(...next.panels);
    done.add(next.zone);
  }
  return path.map((panel) => panel.id);
};
